import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
    extractPriorYearFs,
    savePriorYearFsAliases,
    type PriorYearFsExtractResultVO,
    type PriorYearFsMatchVO,
} from '@/services/accountService/glInvoiceService';
import { fetchLeafGlCategories, type GlCategory } from '@/services/accountService/glCategoryService';
import { useFsCategoryStore } from '@/store/audit/fsCategoryStore';
import { b6Row6Sign } from '@/utils/glToFsMapper';
import MappingAddGlCategoryDialog, {
    type AddGlCategoryPrefill,
} from '@/pages/components/account/gl-invoice/create-stepper/MappingAddGlCategoryDialog';
import { InputField } from '@/pages/components/common/InputField';
import AddIcon from '@mui/icons-material/Add';

const ACCEPT = '.png,.jpg,.jpeg,.webp,.pdf,.xlsx,.xls';

export interface PriorYearPeriodHint {
    /** 期望的上年期间起 yyyy-MM-dd */
    expectedFrom?: string | null;
    /** 期望的上年期间止 / as-at yyyy-MM-dd */
    expectedTo?: string | null;
    /** 展示用文案 */
    expectedLabel?: string | null;
}

export interface PriorYearFsExtractBarProps {
    /**
     * 将抽取到的 summaryPriorYear（fsCode|glCode 或 fsCode|LY）应用到编辑器。
     * GL 列表按 glCode 分列；Last Year FS 仍按 fsCode 合并展示。
     * remarksByFsCode：同 FS 下多项来源的说明（如 Sales + Refund），供 Last Year 行备注。
     */
    onApply: (
        summaryPriorYear: Record<string, number>,
        result: PriorYearFsExtractResultVO,
        remarksByFsCode?: Record<string, string>,
    ) => void;
    disabled?: boolean;
    /** 当前 annual 对应的上年期间，用于 period confirm */
    expectedPeriod?: PriorYearPeriodHint;
    /** 公司 ID：匹配/保存 Co.GL（account_company_gl_category）别名 */
    companyId?: number | null;
    /**
     * `gl`（默认）：可改 FS/GL、勾选 Alias、写入 Co.GL。
     * `fs`：Finance Statement 场景，只映射 FS 并填 Last Year，无 GL / Alias。
     */
    variant?: 'gl' | 'fs';
}

type EditableRow = {
    key: string;
    sourceLabel: string;
    sourceAmount: number;
    fsCode: string | null;
    fsNameEn?: string | null;
    glCode: string | null;
    confidence?: string;
    reason?: string;
    status?: string;
    /** 保存为 Co.GL alias（需有 glCode；写入 company_gl_category） */
    saveAlias: boolean;
    /** 用户改过 FS/GL */
    manual: boolean;
    originalFsCode: string | null;
    originalGlCode: string | null;
};

type FsOption = { code: string; label: string };
type GlOption = { code: string; label: string; fsCode?: string | null };
type EditingCell = { rowKey: string; field: 'fs' | 'gl' } | null;

function confColor(c?: string): 'success' | 'warning' | 'default' | 'error' {
    if (c === 'HIGH' || c === 'MANUAL') return 'success';
    if (c === 'MEDIUM') return 'warning';
    if (c === 'LOW' || c === 'UNMATCHED') return 'error';
    return 'default';
}

/** 中文 / 英文 (编码) */
function formatZhEnCodeLabel(code: string, nameEn?: string | null, nameZh?: string | null): string {
    const en = (nameEn || '').trim();
    const zh = (nameZh || '').trim();
    if (zh && en) return `${zh} / ${en} (${code})`;
    if (zh) return `${zh} (${code})`;
    if (en) return `${en} (${code})`;
    return code;
}

const selectDisplaySx = {
    cursor: 'pointer',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: 1,
    px: 1,
    py: 0.75,
    minHeight: 40,
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    '&:hover': { bgcolor: 'action.hover', borderColor: 'primary.light' },
} as const;

function fmtAmt(n?: number): string {
    if (n == null || !Number.isFinite(n)) return '—';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function normDate(v?: string | null): string {
    if (!v) return '';
    const s = String(v).trim();
    const m = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
    if (m) {
        return `${m[3]}-${m[2].padStart(2, '0')}-${m[1].padStart(2, '0')}`;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    return s;
}

function sameDate(a?: string | null, b?: string | null): boolean {
    const x = normDate(a);
    const y = normDate(b);
    return !!x && !!y && x === y;
}

function toPriorRaw(fsCode: string, displayAmount: number): number {
    const sign = b6Row6Sign(fsCode);
    if (sign === 0 || !Number.isFinite(displayAmount)) return 0;
    return Math.round(sign * displayAmount * 100) / 100;
}

function pickDefaultGl(
    fsCode: string | null | undefined,
    suggestedGlCode: string | null | undefined,
    glLeaves: GlCategory[],
): string | null {
    if (!fsCode) return null;
    const under = glLeaves.filter((c) => (c.fsCode || '').trim() === fsCode.trim() && !!c.code);
    if (suggestedGlCode && under.some((c) => c.code === suggestedGlCode)) {
        return suggestedGlCode;
    }
    if (under.length === 1) return under[0].code || null;
    return null;
}

function matchesToEditableRows(matches: PriorYearFsMatchVO[], glLeaves: GlCategory[]): EditableRow[] {
    return (matches || []).map((m, i) => {
        const fsCode = m.fsCode?.trim() || null;
        const glCode = pickDefaultGl(fsCode, m.suggestedGlCode, glLeaves);
        const filled = !!fsCode && (m.status === 'FILLED' || m.confidence === 'HIGH' || m.confidence === 'MEDIUM');
        return {
            key: `${i}-${m.sourceLabel || ''}`,
            sourceLabel: m.sourceLabel || '',
            sourceAmount: Number(m.sourceAmount) || 0,
            fsCode,
            fsNameEn: m.fsNameEn,
            glCode,
            confidence: m.confidence,
            reason: m.reason,
            status: m.status,
            // Co.GL 别名必须带 GL code
            saveAlias: filled && !!glCode,
            manual: false,
            originalFsCode: fsCode,
            originalGlCode: glCode,
        };
    });
}

function buildSummaryFromRows(rows: EditableRow[], fsOnly = false): Record<string, number> {
    const out: Record<string, number> = {};
    for (const row of rows) {
        if (!row.fsCode) continue;
        const raw = toPriorRaw(row.fsCode, row.sourceAmount);
        if (!Number.isFinite(raw)) continue;
        // FS Finance Statement：始终 fsCode|LY，不按 GL 分列
        const key = !fsOnly && row.glCode ? `${row.fsCode}|${row.glCode}` : `${row.fsCode}|LY`;
        out[key] = Math.round(((out[key] || 0) + raw) * 100) / 100;
    }
    return out;
}

function fmtRemarkAmt(n: number): string {
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('en-US', { maximumFractionDigits: 2, minimumFractionDigits: 0 });
}

/**
 * 每条已映射 Source 都写 Last Year remark：client 名 + 金额。
 * 同 FS 多条用 "; " 拼接；用户改了 FS 后按当前映射重算。
 * 原 FS 若已无任何行，写入空串以便 Apply 清掉旧 remark。
 */
export function buildRemarksByFsCode(rows: EditableRow[]): Record<string, string> {
    const groups = new Map<string, { label: string; amount: number }[]>();
    const abandoned = new Set<string>();
    for (const row of rows) {
        const fs = (row.fsCode || '').trim();
        const original = (row.originalFsCode || '').trim();
        if (original && original !== fs) abandoned.add(original);
        if (!fs) continue;
        const label = (row.sourceLabel || '').trim() || '(blank)';
        const list = groups.get(fs) || [];
        list.push({ label, amount: Number(row.sourceAmount) || 0 });
        groups.set(fs, list);
    }
    const out: Record<string, string> = {};
    for (const [fs, items] of groups) {
        out[fs] = items.map((i) => `${i.label} (${fmtRemarkAmt(i.amount)})`).join('; ');
        abandoned.delete(fs);
    }
    for (const fs of abandoned) {
        out[fs] = '';
    }
    return out;
}

/**
 * Last Year：上传上年 BS/PL → 抽取匹配预览 → Period confirm → Apply。
 * `gl`：可改 FS/GL、勾选 Alias；`fs`：只改 FS，金额填入 Last Year。
 */
export default function PriorYearFsExtractBar({
    onApply, disabled, expectedPeriod, companyId, variant = 'gl',
}: PriorYearFsExtractBarProps) {
    const fsOnly = variant === 'fs';
    const inputRef = useRef<HTMLInputElement>(null);
    const [extracting, setExtracting] = useState(false);
    const [applying, setApplying] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [preview, setPreview] = useState<PriorYearFsExtractResultVO | null>(null);
    const [rows, setRows] = useState<EditableRow[]>([]);
    const [confirmFrom, setConfirmFrom] = useState('');
    const [confirmTo, setConfirmTo] = useState('');
    const [periodConfirmed, setPeriodConfirmed] = useState(false);
    const [glLeaves, setGlLeaves] = useState<GlCategory[]>([]);
    const [addGlRowKey, setAddGlRowKey] = useState<string | null>(null);
    /** 点击才编辑 FS/GL，样式对齐 Column Mapping */
    const [editingCell, setEditingCell] = useState<EditingCell>(null);
    const byCode = useFsCategoryStore((s) => s.byCode);

    useEffect(() => {
        if (fsOnly) {
            setGlLeaves([]);
            return;
        }
        let cancelled = false;
        fetchLeafGlCategories()
            .then((list) => {
                if (!cancelled) setGlLeaves(list || []);
            })
            .catch(() => {
                if (!cancelled) setGlLeaves([]);
            });
        return () => { cancelled = true; };
    }, [fsOnly]);

    const fsOptions: FsOption[] = useMemo(() => {
        const out: FsOption[] = [];
        for (const [code, line] of Object.entries(byCode || {})) {
            if (!(code.startsWith('BS-') || code.startsWith('PL-'))) continue;
            if ((code.match(/-/g) || []).length < 2) continue;
            out.push({
                code,
                label: formatZhEnCodeLabel(code, line?.nameEn, line?.nameZh),
            });
        }
        out.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        return out;
    }, [byCode]);

    const glOptionsByFs = useMemo(() => {
        const map = new Map<string, GlOption[]>();
        for (const c of glLeaves) {
            const fs = (c.fsCode || '').trim();
            const code = (c.code || '').trim();
            if (!fs || !code) continue;
            const list = map.get(fs) || [];
            list.push({
                code,
                label: formatZhEnCodeLabel(code, c.nameEn, c.nameZh),
                fsCode: fs,
            });
            map.set(fs, list);
        }
        for (const list of map.values()) {
            list.sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
        }
        return map;
    }, [glLeaves]);

    const handleFiles = useCallback(async (files: FileList | File[] | null) => {
        if (!files || files.length === 0) return;
        setError(null);
        setPeriodConfirmed(false);
        setExtracting(true);
        try {
            const list = Array.from(files);
            const result = await extractPriorYearFs(list, companyId);
            setPreview(result);
            const editable = matchesToEditableRows(result.matches || [], fsOnly ? [] : glLeaves);
            setRows(fsOnly
                ? editable.map((r) => ({ ...r, glCode: null, saveAlias: false, originalGlCode: null }))
                : editable);
            const detectedTo = normDate(result.detectedPeriodTo || result.detectedAsAtDate);
            const detectedFrom = normDate(result.detectedPeriodFrom);
            const expectedTo = normDate(expectedPeriod?.expectedTo);
            const expectedFrom = normDate(expectedPeriod?.expectedFrom);
            setConfirmTo(detectedTo || expectedTo || '');
            setConfirmFrom(detectedFrom || expectedFrom || '');
            setPeriodConfirmed(false);
        } catch (e: any) {
            setError(e?.message || 'Extract failed');
            setPreview(null);
            setRows([]);
        } finally {
            setExtracting(false);
            if (inputRef.current) inputRef.current.value = '';
        }
    }, [companyId, expectedPeriod?.expectedFrom, expectedPeriod?.expectedTo, glLeaves, fsOnly]);

    useEffect(() => {
        if (!preview) {
            setPeriodConfirmed(false);
            setConfirmFrom('');
            setConfirmTo('');
            setRows([]);
            setEditingCell(null);
        }
    }, [preview]);

    // glLeaves 晚到时补默认 GL
    useEffect(() => {
        if (!preview || !glLeaves.length || !rows.length) return;
        setRows((prev) => {
            let changed = false;
            const next = prev.map((row) => {
                if (row.glCode || !row.fsCode || row.manual) return row;
                const gl = pickDefaultGl(row.fsCode, row.originalGlCode, glLeaves);
                if (!gl || gl === row.glCode) return row;
                changed = true;
                return { ...row, glCode: gl, originalGlCode: gl };
            });
            return changed ? next : prev;
        });
    }, [glLeaves, preview]); // eslint-disable-line react-hooks/exhaustive-deps

    const periodMismatch = useMemo(() => {
        if (!preview) return false;
        const expectedTo = normDate(expectedPeriod?.expectedTo);
        const expectedFrom = normDate(expectedPeriod?.expectedFrom);
        if (!expectedTo && !expectedFrom) return false;
        const toOk = !expectedTo || !confirmTo || sameDate(confirmTo, expectedTo);
        const fromOk = !expectedFrom || !confirmFrom || sameDate(confirmFrom, expectedFrom);
        return !(toOk && fromOk);
    }, [preview, expectedPeriod, confirmFrom, confirmTo]);

    const summaryPriorYear = useMemo(() => buildSummaryFromRows(rows, fsOnly), [rows, fsOnly]);
    const filledKeys = Object.keys(summaryPriorYear).length;
    const aliasCount = fsOnly
        ? 0
        : rows.filter((r) => r.saveAlias && r.glCode && r.sourceLabel.trim()).length;
    const existingGlCodes = useMemo(
        () => new Set(glLeaves.map((c) => (c.code || '').trim()).filter(Boolean)),
        [glLeaves],
    );
    const addGlPrefill: AddGlCategoryPrefill | null = useMemo(() => {
        if (!addGlRowKey) return null;
        const row = rows.find((r) => r.key === addGlRowKey);
        if (!row?.fsCode) return null;
        const siblings = glLeaves.filter((c) => (c.fsCode || '').trim() === row.fsCode);
        return {
            nameEn: row.sourceLabel || 'New',
            fsCode: row.fsCode,
            template: siblings[0] || null,
        };
    }, [addGlRowKey, rows, glLeaves]);
    const lyFallbackCount = rows.filter((r) => r.fsCode && !r.glCode).length;
    const canApply = filledKeys > 0 && periodConfirmed && !applying;

    const updateRow = useCallback((key: string, patch: Partial<EditableRow>) => {
        setRows((prev) => prev.map((r) => {
            if (r.key !== key) return r;
            const next = { ...r, ...patch };
            if (patch.fsCode !== undefined && patch.fsCode !== r.fsCode) {
                next.manual = true;
                next.confidence = 'MANUAL';
                next.reason = 'Manual FS';
                next.status = patch.fsCode ? 'FILLED' : 'UNMATCHED';
                if (!fsOnly) {
                    const gl = pickDefaultGl(patch.fsCode, null, glLeaves);
                    next.glCode = gl;
                    next.saveAlias = !!gl;
                } else {
                    next.glCode = null;
                    next.saveAlias = false;
                }
                next.fsNameEn = patch.fsCode
                    ? (useFsCategoryStore.getState().byCode[patch.fsCode]?.nameEn ?? null)
                    : null;
            }
            if (!fsOnly && patch.glCode !== undefined && patch.glCode !== r.glCode) {
                next.manual = true;
                next.saveAlias = !!patch.glCode;
                if (next.confidence !== 'MANUAL') {
                    next.confidence = 'MANUAL';
                    next.reason = 'Manual GL';
                }
            }
            if (!fsOnly && patch.saveAlias !== undefined) {
                next.saveAlias = patch.saveAlias;
            }
            return next;
        }));
    }, [glLeaves, fsOnly]);

    const handleApply = useCallback(async () => {
        if (!preview || !periodConfirmed || filledKeys <= 0) return;
        setApplying(true);
        setError(null);
        try {
            if (!fsOnly && companyId && aliasCount > 0) {
                // Source → Co.GL aliasEn；写入 account_company_gl_category
                const items = rows
                    .filter((r) => r.saveAlias && r.glCode && r.sourceLabel.trim())
                    .map((r) => ({
                        sourceLabel: r.sourceLabel.trim(),
                        fsCode: r.fsCode || '',
                        glCode: r.glCode!,
                    }));
                await savePriorYearFsAliases(companyId, items);
            }
            const patched: PriorYearFsExtractResultVO = {
                ...preview,
                summaryPriorYear,
                matches: rows.map((r) => ({
                    sourceLabel: r.sourceLabel,
                    sourceAmount: r.sourceAmount,
                    fsCode: r.fsCode || undefined,
                    fsNameEn: r.fsNameEn || undefined,
                    suggestedGlCode: r.glCode,
                    confidence: r.confidence,
                    status: r.fsCode ? 'FILLED' : 'UNMATCHED',
                    reason: r.reason,
                    priorRawAmount: r.fsCode ? toPriorRaw(r.fsCode, r.sourceAmount) : undefined,
                })),
                filledCount: rows.filter((r) => r.fsCode).length,
                unmatchedCount: rows.filter((r) => !r.fsCode).length,
            };
            const remarksByFsCode = buildRemarksByFsCode(rows);
            onApply(summaryPriorYear, patched, remarksByFsCode);
            setPreview(null);
            setRows([]);
        } catch (e: any) {
            setError(e?.message || 'Apply failed');
        } finally {
            setApplying(false);
        }
    }, [
        preview, periodConfirmed, filledKeys, companyId, aliasCount, rows,
        summaryPriorYear, onApply, fsOnly,
    ]);

    const expectedLabel = expectedPeriod?.expectedLabel
        || (expectedPeriod?.expectedFrom || expectedPeriod?.expectedTo
            ? `${expectedPeriod?.expectedFrom || '?'} ~ ${expectedPeriod?.expectedTo || '?'}`
            : null);

    return (
        <Box sx={{ mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                    size="small"
                    variant="outlined"
                    startIcon={extracting ? <CircularProgress size={14} /> : <UploadFileIcon />}
                    disabled={disabled || extracting}
                    onClick={() => inputRef.current?.click()}
                    sx={{ textTransform: 'none' }}
                >
                    {extracting ? 'AI analysing…' : 'AI extract last-year BS/PL'}
                </Button>
                <Typography variant="caption" color="text.secondary">
                    {fsOnly
                        ? '可多选 BS+PL。确认期间后，将识别金额填入 Last Year。'
                        : '可多选 BS+PL。勾选 Alias 将 Source 写入 Co.GL（company_gl_category）；无 GL 时可 Add。'}
                </Typography>
                <input
                    ref={inputRef}
                    type="file"
                    hidden
                    multiple
                    accept={ACCEPT}
                    onChange={(e) => handleFiles(e.target.files)}
                />
            </Stack>
            {error && (
                <Alert severity="error" sx={{ mt: 1 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            <Dialog
                open={!!preview}
                onClose={() => !extracting && !applying && setPreview(null)}
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: { width: '96vw', maxWidth: fsOnly ? 1200 : 1680 },
                }}
            >
                <DialogTitle>
                    {fsOnly ? 'Last-year FS AI extract → Last Year' : 'Last-year FS AI extract preview'}
                </DialogTitle>
                <DialogContent dividers>
                    {preview && (
                        <Stack spacing={1.5}>
                            <Alert severity={periodMismatch ? 'warning' : 'info'}>
                                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                                    Period confirm
                                </Typography>
                                <Typography variant="body2" sx={{ mb: 0.5 }}>
                                    Detected:{' '}
                                    {preview.detectedPeriodLabel
                                        || (preview.detectedPeriodFrom || preview.detectedPeriodTo
                                            ? `${preview.detectedPeriodFrom || '?'} ~ ${preview.detectedPeriodTo || preview.detectedAsAtDate || '?'}`
                                            : '— (not found on document)')}
                                </Typography>
                                {expectedLabel && (
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        Expected last-year period: {expectedLabel}
                                    </Typography>
                                )}
                                {periodMismatch && (
                                    <Typography variant="body2" color="warning.dark" sx={{ mb: 1 }}>
                                        Detected period does not match the company&apos;s last-year period. Please verify before apply.
                                    </Typography>
                                )}
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mb: 1 }}>
                                    <TextField
                                        size="small"
                                        label="Confirm period from"
                                        type="date"
                                        value={confirmFrom}
                                        onChange={(e) => {
                                            setConfirmFrom(e.target.value);
                                            setPeriodConfirmed(false);
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 180 }}
                                    />
                                    <TextField
                                        size="small"
                                        label="Confirm period to / as at"
                                        type="date"
                                        value={confirmTo}
                                        onChange={(e) => {
                                            setConfirmTo(e.target.value);
                                            setPeriodConfirmed(false);
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ minWidth: 180 }}
                                    />
                                </Stack>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={periodConfirmed}
                                            onChange={(e) => setPeriodConfirmed(e.target.checked)}
                                            size="small"
                                        />
                                    }
                                    label={
                                        confirmTo
                                            ? `I confirm these figures belong to the period ending ${confirmTo}`
                                            : 'I confirm these figures belong to the last-year comparative period'
                                    }
                                />
                            </Alert>

                            <Typography variant="body2">
                                Rows with FS: {rows.filter((r) => r.fsCode).length} ·
                                Keys to apply: {filledKeys}
                                {!fsOnly && (
                                    <>
                                        {' · '}Save aliases: {aliasCount}
                                        {lyFallbackCount > 0 ? ` · ${lyFallbackCount} row(s) use |LY (no GL)` : ''}
                                    </>
                                )}
                            </Typography>
                            {(preview.warnings?.length ?? 0) > 0 && (
                                <Alert severity="warning">
                                    <Box component="ul" sx={{ m: 0, pl: 2 }}>
                                        {preview.warnings.map((w, i) => (
                                            <Typography key={i} component="li" variant="body2">{w}</Typography>
                                        ))}
                                    </Box>
                                </Alert>
                            )}
                            <Box sx={{ overflowX: 'auto' }}>
                                <Table size="small" stickyHeader sx={{ minWidth: fsOnly ? 720 : 1280 }}>
                                    <TableHead>
                                        <TableRow>
                                            {!fsOnly && (
                                                <TableCell padding="checkbox" title="Save Source as Co.GL aliasEn">
                                                    Alias
                                                </TableCell>
                                            )}
                                            <TableCell sx={{ minWidth: 200 }}>Source</TableCell>
                                            <TableCell align="right" sx={{ minWidth: 110 }}>Amount</TableCell>
                                            <TableCell sx={{ minWidth: 280 }}>FS</TableCell>
                                            {!fsOnly && (
                                                <>
                                                    <TableCell sx={{ width: 28, px: 0 }} />
                                                    <TableCell sx={{ minWidth: 300 }}>GL</TableCell>
                                                </>
                                            )}
                                            <TableCell sx={{ minWidth: 100 }}>Confidence</TableCell>
                                            <TableCell sx={{ minWidth: 180 }}>Note</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((row) => {
                                            const glOpts = !fsOnly && row.fsCode
                                                ? (glOptionsByFs.get(row.fsCode) || [])
                                                : [];
                                            const fsValue = fsOptions.find((o) => o.code === row.fsCode) || null;
                                            const glValue = glOpts.find((o) => o.code === row.glCode) || null;
                                            const fsEditing = editingCell?.rowKey === row.key && editingCell.field === 'fs';
                                            const glEditing = !fsOnly && editingCell?.rowKey === row.key && editingCell.field === 'gl';
                                            return (
                                                <TableRow key={row.key}>
                                                    {!fsOnly && (
                                                        <TableCell padding="checkbox">
                                                            <Checkbox
                                                                size="small"
                                                                checked={row.saveAlias}
                                                                disabled={!row.glCode}
                                                                onChange={(e) => updateRow(row.key, { saveAlias: e.target.checked })}
                                                            />
                                                        </TableCell>
                                                    )}
                                                    <TableCell sx={{ minWidth: 200 }} title={row.sourceLabel}>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                            {row.sourceLabel || '—'}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">{fmtAmt(row.sourceAmount)}</TableCell>
                                                    <TableCell sx={{ minWidth: 280, verticalAlign: 'middle' }}>
                                                        {fsEditing ? (
                                                            <Autocomplete
                                                                size="small"
                                                                openOnFocus
                                                                options={fsOptions}
                                                                value={fsValue}
                                                                onChange={(_, v) => {
                                                                    updateRow(row.key, { fsCode: v?.code || null });
                                                                    setEditingCell(null);
                                                                }}
                                                                onClose={() => setEditingCell(null)}
                                                                getOptionLabel={(o) => o.label}
                                                                isOptionEqualToValue={(a, b) => a.code === b.code}
                                                                ListboxProps={{ style: { maxHeight: 320 } }}
                                                                componentsProps={{
                                                                    paper: { sx: { minWidth: 420, maxWidth: 560 } },
                                                                }}
                                                                renderOption={(props, option) => (
                                                                    <li {...props} key={option.code}>
                                                                        <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                            {option.label}
                                                                        </Typography>
                                                                    </li>
                                                                )}
                                                                renderInput={(params) => (
                                                                    <InputField
                                                                        {...params}
                                                                        placeholder="ZH / EN (code)"
                                                                        align="left"
                                                                        autoFocus
                                                                        inputProps={{
                                                                            ...params.inputProps,
                                                                            title: fsValue?.label || undefined,
                                                                        }}
                                                                    />
                                                                )}
                                                                sx={{ width: '100%' }}
                                                            />
                                                        ) : (
                                                            <Tooltip title={fsValue?.label || 'Click to select FS'}>
                                                                <Box
                                                                    onClick={() => setEditingCell({ rowKey: row.key, field: 'fs' })}
                                                                    sx={selectDisplaySx}
                                                                >
                                                                    <Typography
                                                                        variant="body2"
                                                                        noWrap
                                                                        color={fsValue ? 'text.primary' : 'text.secondary'}
                                                                        sx={{ width: '100%' }}
                                                                    >
                                                                        {fsValue?.label || 'Select FS'}
                                                                    </Typography>
                                                                </Box>
                                                            </Tooltip>
                                                        )}
                                                    </TableCell>
                                                    {!fsOnly && (
                                                        <>
                                                            <TableCell sx={{ width: 28, px: 0.5, textAlign: 'center', verticalAlign: 'middle' }}>
                                                                <Typography variant="body2" color="text.secondary">→</Typography>
                                                            </TableCell>
                                                            <TableCell sx={{ minWidth: 300, verticalAlign: 'middle' }}>
                                                                <Stack direction="row" spacing={1} alignItems="center">
                                                                    {glEditing ? (
                                                                        <Autocomplete
                                                                            size="small"
                                                                            openOnFocus
                                                                            options={glOpts}
                                                                            value={glValue}
                                                                            onChange={(_, v) => {
                                                                                updateRow(row.key, { glCode: v?.code || null });
                                                                                setEditingCell(null);
                                                                            }}
                                                                            onClose={() => setEditingCell(null)}
                                                                            getOptionLabel={(o) => o.label}
                                                                            isOptionEqualToValue={(a, b) => a.code === b.code}
                                                                            disabled={!row.fsCode}
                                                                            ListboxProps={{ style: { maxHeight: 320 } }}
                                                                            componentsProps={{
                                                                                paper: { sx: { minWidth: 420, maxWidth: 560 } },
                                                                            }}
                                                                            renderOption={(props, option) => (
                                                                                <li {...props} key={option.code}>
                                                                                    <Typography variant="body2" sx={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>
                                                                                        {option.label}
                                                                                    </Typography>
                                                                                </li>
                                                                            )}
                                                                            renderInput={(params) => (
                                                                                <InputField
                                                                                    {...params}
                                                                                    placeholder={glOpts.length ? 'ZH / EN (code)' : 'No GL — Add'}
                                                                                    align="left"
                                                                                    autoFocus
                                                                                    inputProps={{
                                                                                        ...params.inputProps,
                                                                                        title: glValue?.label || undefined,
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            sx={{ flex: 1, minWidth: 0 }}
                                                                        />
                                                                    ) : (
                                                                        <Tooltip title={
                                                                            !row.fsCode
                                                                                ? 'Select FS first'
                                                                                : (glValue?.label || 'Click to select GL Category')
                                                                        }>
                                                                            <Box
                                                                                onClick={() => {
                                                                                    if (!row.fsCode) return;
                                                                                    setEditingCell({ rowKey: row.key, field: 'gl' });
                                                                                }}
                                                                                sx={{
                                                                                    ...selectDisplaySx,
                                                                                    flex: 1,
                                                                                    minWidth: 0,
                                                                                    ...(!row.fsCode ? { opacity: 0.5, cursor: 'not-allowed' } : {}),
                                                                                }}
                                                                            >
                                                                                <Typography
                                                                                    variant="body2"
                                                                                    noWrap
                                                                                    color={glValue ? 'text.primary' : 'text.secondary'}
                                                                                    sx={{ width: '100%' }}
                                                                                >
                                                                                    {glValue?.label || 'Select GL Category'}
                                                                                </Typography>
                                                                            </Box>
                                                                        </Tooltip>
                                                                    )}
                                                                    {row.fsCode && (
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => setAddGlRowKey(row.key)}
                                                                            sx={{ textTransform: 'none', whiteSpace: 'nowrap', flexShrink: 0 }}
                                                                            title="Add new GL under this FS"
                                                                        >
                                                                            Add GL
                                                                        </Button>
                                                                    )}
                                                                </Stack>
                                                            </TableCell>
                                                        </>
                                                    )}
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            label={row.confidence || row.status || '—'}
                                                            color={confColor(row.confidence || row.status)}
                                                            variant="outlined"
                                                        />
                                                    </TableCell>
                                                    <TableCell sx={{ minWidth: 180 }}>
                                                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'normal' }}>
                                                            {row.reason || ''}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {rows.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={fsOnly ? 5 : 8}>
                                                    <Typography variant="body2" color="text.secondary">No rows extracted.</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </Box>
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPreview(null)}
                        disabled={applying}
                        sx={{ textTransform: 'none' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!canApply}
                        onClick={handleApply}
                        startIcon={applying ? <CircularProgress size={14} color="inherit" /> : undefined}
                        sx={{ textTransform: 'none' }}
                    >
                        {!periodConfirmed
                            ? 'Confirm period to apply'
                            : fsOnly
                                ? `Apply to Last Year (${filledKeys})`
                                : aliasCount > 0
                                    ? `Apply to Last Year (${filledKeys}) · save ${aliasCount} aliases`
                                    : `Apply to Last Year (${filledKeys})`}
                    </Button>
                </DialogActions>
            </Dialog>

            {!fsOnly && (
                <MappingAddGlCategoryDialog
                    open={!!addGlPrefill}
                    prefill={addGlPrefill}
                    existingCodes={existingGlCodes}
                    onClose={() => setAddGlRowKey(null)}
                    onCreated={(created) => {
                        if (!created.code) return;
                        setGlLeaves((prev) => (
                            prev.some((c) => c.code === created.code) ? prev : [...prev, created]
                        ));
                        if (addGlRowKey) {
                            updateRow(addGlRowKey, { glCode: created.code });
                        }
                        setAddGlRowKey(null);
                    }}
                />
            )}
        </Box>
    );
}
